import { useState, useEffect, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { Plus, Trash, FloppyDisk, CaretDown, CaretRight, CheckCircle, WarningCircle,
         UploadSimple, ArrowsClockwise, Buildings, Table, Calculator, X, Spinner, DownloadSimple } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { inferKgPerUnit } from './mat3Density'

// ── Estilos base ──────────────────────────────────────────────────────────────
const C = {
  bg: '#F5F0EA', card: '#FFFFFF', border: '#DDD5C8',
  dark: '#3B2F2F', brown: '#5C4A3A', muted: '#7A6A5A',
  olive: '#8D9A2E', coral: '#D4614A', amber: '#C98A2E', cream: '#FAF7F3',
  blue: '#3B7EC8',
}
const CARD  = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)' }
const INP   = { padding: '0.35rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif", color: C.dark, background: C.cream, outline: 'none', width: '100%', boxSizing: 'border-box' }
const BTN_P = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', background: C.olive, color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', fontFamily: "'DM Sans', sans-serif" }
const BTN_G = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif" }

// ── Constantes BREEAM ES MAT 3 ────────────────────────────────────────────────
const ELEMENTS_LIST = [
  { key: 'estructura',         label: 'Estructura' },
  { key: 'losa_pb',            label: 'Losa o forjado de planta baja' },
  { key: 'forjados',           label: 'Forjados de plantas superiores' },
  { key: 'cubiertas',          label: 'Cubiertas' },
  { key: 'fachadas',           label: 'Fachadas' },
  { key: 'particiones',        label: 'Particiones interiores verticales y medianeras' },
  { key: 'cimientos',          label: 'Cimientos' },
  { key: 'acondicionamientos', label: 'Acondicionamientos' },
  { key: 'paisaje',            label: 'Paisaje ajardinado duro' },
]

const ELEMENT_LABEL = Object.fromEntries(ELEMENTS_LIST.map(e => [e.key, e.label]))

// Niveles de certificación (Tabla 37 BREEAM ES NC 2015)
const TIER_INFO = [
  { value: 1, label: 'Nivel 1 – BES 6001 / FSC / PEFC', points: 3.0, color: C.olive },
  { value: 2, label: 'Nivel 2 – Esquema responsable',    points: 2.0, color: C.amber },
  { value: 3, label: 'Nivel 3 – ISO 14001 / EMAS (SGA)', points: 1.5, color: C.blue },
  { value: 4, label: 'Nivel 4 – Sin certificación',      points: 0.0, color: C.coral },
]
const TIER_POINTS = { 1: 3.0, 2: 2.0, 3: 1.5, 4: 0.0 }

// Certificaciones conocidas por defecto (MAT3.2)
// Tabla por defecto: VACÍA. Cada proyecto debe rellenar sus propias empresas
// con el botón "+ Añadir empresa" o importando desde la Biblioteca/Excel del proyecto.
// (Antes existía un catálogo hardcoded de 24 empresas históricas de ABAMA — se eliminó
// para evitar que esos datos se propaguen a proyectos distintos que no los necesitan.)
const DEFAULT_CERTS = []

// Factores de densidad por defecto (unidad → kg)
const DEFAULT_DENSITY = { m3: 2400, t: 1000, kg: 1, m2: null, m: null, ud: null, ml: null }

// ── Helpers estado ────────────────────────────────────────────────────────────
function newMat() { return { id: Math.random(), name: '', pct: '', tier: 3 } }
function newSpec() { return { id: Math.random(), weight: '100', materials: [newMat()] } }
function newElem(key) { return { key, presente: false, specs: [newSpec()] } }
function newPartida() {
  return { id: Math.random(), code: '', description: '', empresa: '', unit: 'kg', qty: '', densityFactor: '', elementBREEAM: 'estructura', source: 'manual' }
}

// Versión del algoritmo de generación de elements. Bump cuando cambie la lógica de
// partidasToElements para invalidar el cache local de sesiones antiguas (BUG-013, catálogo densidades).
const ELEMENTS_ALGO_VERSION = 'v2-density-catalog-2026-05-21'

function loadState(pid) {
  try { return JSON.parse(localStorage.getItem(`mat3_${pid}`) || 'null') } catch { return null }
}
function saveState(pid, s) {
  try { localStorage.setItem(`mat3_${pid}`, JSON.stringify({ ...s, _elementsAlgoVersion: ELEMENTS_ALGO_VERSION })) } catch {}
}

// ── Cálculo puntos MAT3 ───────────────────────────────────────────────────────
// Replica la fórmula oficial del Excel ITG (BUG-013 + BUG-014):
//   I_i = ROUND(pct_i, 0) si nivel ≤ 3, else 0   (columna I del Excel)
//   R22 = SUM(I_i)                                (% total responsable, ya redondeado)
//   N_i = I_i × puntos_nivel_i / R22              (puntos del material, redondeado 2 dec)
//   total_elem = SUM(N_i)
//   passes80 = R22 ≥ 80
// Esta normalización por R22 (en lugar de /100) RE-DISTRIBUYE los puntos sobre la
// parte responsable: cuando hay materiales L4, el cálculo oficial puntúa un poco más
// que dividir por 100, lo que es coherente con la calculadora oficial homologada.
function calcElement(elem) {
  // 1) Aplicar peso de la spec al % de cada material y derivar I_i (% redondeado responsable)
  const matsFlat = []
  for (const spec of elem.specs) {
    const w = parseFloat(spec.weight) || 0
    if (!w) continue
    for (const m of spec.materials) {
      const pctSpec = Math.max(0, parseFloat(m.pct) || 0)
      if (!pctSpec) continue
      const pctElem = pctSpec * w / 100
      const tier = parseInt(m.tier) || 4
      const iVal = tier <= 3 ? Math.round(pctElem) : 0
      matsFlat.push({ pctElem, tier, iVal })
    }
  }
  // 2) R22 = suma de I_i (total % responsable, redondeado por material)
  const r22 = matsFlat.reduce((s, x) => s + x.iVal, 0)
  // 3) Cálculo puntos por material y total
  let rawPts = 0
  if (r22 > 0) {
    for (const x of matsFlat) {
      if (x.iVal <= 0) continue
      const pointsPerTier = TIER_POINTS[x.tier] || 0
      const n_i = Math.round(((x.iVal * pointsPerTier) / r22) * 100) / 100  // 2 decimales
      rawPts += n_i
    }
  }
  // 4) Regla del 80% sobre R22 (% responsable total)
  const passes80 = r22 >= 80
  return { rawPts, elementPts: passes80 ? rawPts : 0, responsible: r22, passes80 }
}
function calcMAT3(elements) {
  let totalAvail = 0, totalEarned = 0
  const results = {}
  for (const elem of elements) {
    if (!elem.presente) continue
    totalAvail += 3
    const r = calcElement(elem)
    results[elem.key] = r
    totalEarned += r.elementPts
  }
  const pct = totalAvail > 0 ? totalEarned / totalAvail * 100 : 0
  let breeamPts = 0, innovPts = 0
  if (pct >= 70) { breeamPts = 3; innovPts = 1 }
  else if (pct >= 54) breeamPts = 3
  else if (pct >= 36) breeamPts = 2
  else if (pct >= 18) breeamPts = 1
  return { totalAvail, totalEarned, pct, breeamPts, innovPts, results }
}

// ── Convertir partidas → elementos calculadora ───────────────────────────────
function partidasToElements(partidas, certifications) {
  const certMap = {}
  for (const c of certifications) certMap[c.empresa.toLowerCase().trim()] = c.level

  function getLevel(empresa) {
    if (!empresa) return 4
    const key = empresa.toLowerCase().trim()
    // Try exact match first, then partial
    if (certMap[key]) return certMap[key]
    for (const [k, v] of Object.entries(certMap)) {
      if (key.includes(k) || k.includes(key)) return v
    }
    return 4
  }

  // Group by element
  const grouped = {}
  for (const p of partidas) {
    const key = p.elementBREEAM
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(p)
  }

  return ELEMENTS_LIST.map(({ key }) => {
    const group = grouped[key] || []
    if (!group.length) return newElem(key)
    // BUG-013 fix: agrupar por EMPRESA antes de slice(0,8). Antes tomaba las 8 primeras
    // partidas tal cual (perdiendo info en elementos con muchas partidas y forzando renormalización).
    // Ahora: kg por empresa, ordenar desc, top-7 + slot 8 "Otros (N empresas)" si hay >8.
    // CATÁLOGO DE DENSIDADES: usa inferKgPerUnit(descripción, unidad) que devuelve kg/ud
    // realistas (sanitarios 30 kg/ud, puertas 50 kg/ud, baldosa porcelánica 22 kg/m², etc.).
    // Si la partida es aislante (NA8 manual) → se excluye (se evalúa en MAT 4).
    const byEmp = new Map()
    for (const p of group) {
      const emp = (p.empresa || '').trim()
      if (!emp) continue
      let factor = parseFloat(p.densityFactor) || null
      if (!factor) {
        const inferred = inferKgPerUnit(p.description || '', p.unit || '')
        if (inferred === 'EXCLUDE') continue // aislante → MAT 4
        factor = inferred || DEFAULT_DENSITY[p.unit] || 1
      }
      const kg = (parseFloat(p.qty) || 0) * factor
      if (kg <= 0) continue
      byEmp.set(emp, (byEmp.get(emp) || 0) + kg)
    }
    const totalKg = Array.from(byEmp.values()).reduce((s, v) => s + v, 0)
    if (totalKg <= 0) return newElem(key)
    const sorted = Array.from(byEmp.entries()).sort((a, b) => b[1] - a[1])
    const top = sorted.length <= 8 ? sorted : sorted.slice(0, 7)
    const rest = sorted.length <= 8 ? [] : sorted.slice(7)
    const mats = top.map(([emp, kg]) => ({
      id: Math.random(),
      name: emp,
      pct: ((kg / totalKg) * 100).toFixed(2),
      tier: getLevel(emp),
    }))
    if (rest.length > 0) {
      const restKg = rest.reduce((s, [, kg]) => s + kg, 0)
      const restLevels = rest.map(([emp]) => getLevel(emp))
      const worstLevel = restLevels.length ? Math.max(...restLevels) : 4
      mats.push({
        id: Math.random(),
        name: `Otros (${rest.length} empresas)`,
        pct: ((restKg / totalKg) * 100).toFixed(2),
        tier: worstLevel,
      })
    }
    if (!mats.length) return newElem(key)
    return { key, presente: true, specs: [{ id: Math.random(), weight: '100', materials: mats }] }
  })
}

// ── Dividir PDF en chunks ─────────────────────────────────────────────────────
async function splitPdf(file, maxPages = 75) {
  const buf = await file.arrayBuffer()
  const srcDoc = await PDFDocument.load(buf, { ignoreEncryption: true })
  const total = srcDoc.getPageCount()
  if (total <= maxPages) return [{ file, name: file.name, pages: total, chunkIndex: 0, chunkTotal: 1 }]
  const parts = Math.ceil(total / maxPages)
  const chunks = []
  for (let i = 0; i < parts; i++) {
    const start = i * maxPages
    const end = Math.min(start + maxPages, total)
    const chunkDoc = await PDFDocument.create()
    const indices = Array.from({ length: end - start }, (_, j) => start + j)
    const pages = await chunkDoc.copyPages(srcDoc, indices)
    pages.forEach(p => chunkDoc.addPage(p))
    const bytes = await chunkDoc.save()
    const base = file.name.replace(/\.pdf$/i, '')
    const name = `${base}_parte${i + 1}de${parts}.pdf`
    chunks.push({ file: new File([bytes], name, { type: 'application/pdf' }), name, pages: end - start, chunkIndex: i, chunkTotal: parts })
  }
  return chunks
}

// ── Componente principal ──────────────────────────────────────────────────────
async function saveCertsToSupabase(projectId, certs) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr) { console.error('saveCerts auth error:', authErr.message); return }
    if (!user) { console.error('saveCerts: no user'); return }
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Materiales',
      criteria_code: 'MAT3-CERTS',
      criteria_name: 'MAT3 Certificaciones Aprovisionamiento',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(certs),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveCerts upsert error:', error.message, error)
    else console.log('saveCerts OK, certs guardadas:', certs.length)
  } catch (e) { console.error('saveCertsToSupabase exception:', e.message) }
}

async function loadCertsFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'MAT3-CERTS').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) { /* no hay datos guardados */ }
  return null
}

async function savePartidasToSupabase(projectId, partidas) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr) { console.error('savePartidas auth error:', authErr.message); return }
    if (!user) { console.error('savePartidas: no user'); return }
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Materiales',
      criteria_code: 'MAT3-PARTIDAS',
      criteria_name: 'MAT3 Mediciones',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(partidas),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('savePartidas upsert error:', error.message, error)
    else console.log('savePartidas OK, partidas guardadas:', partidas.length)
  } catch (e) { console.error('savePartidasToSupabase exception:', e.message) }
}

async function loadPartidasFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'MAT3-PARTIDAS').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) { /* no hay datos guardados */ }
  return null
}

export default function MAT3Calculator({ projectId, onSave, saving }) {
  const saved = loadState(projectId)
  const [tab, setTab] = useState('aprovisionamiento')
  // Ref para controlar el auto-cálculo de elements; debe declararse ANTES de los useEffect
  // de carga para que el reset al cambiar projectId funcione.
  const autoAppliedRef = useRef(false)

  // Tab 1: Aprovisionamiento
  const [certs, setCerts] = useState(() => saved?.certs || DEFAULT_CERTS)

  // Cargar certs desde Supabase al montar/cambiar proyecto. Resetea SIEMPRE para
  // evitar arrastre de datos del proyecto anterior si el componente persiste en memoria.
  useEffect(() => {
    let cancel = false
    loadCertsFromSupabase(projectId).then(remote => {
      if (cancel) return
      setCerts(remote || DEFAULT_CERTS)
      autoAppliedRef.current = false  // permite recalcular elements para el nuevo proyecto
    })
    return () => { cancel = true }
  }, [projectId])

  // Tab 2: Mediciones
  const [partidas, setPartidas] = useState(() => saved?.partidas || [])

  // Cargar partidas — mismo patrón: resetea SIEMPRE.
  useEffect(() => {
    let cancel = false
    loadPartidasFromSupabase(projectId).then(remote => {
      if (cancel) return
      setPartidas(remote || [])
      autoAppliedRef.current = false
    })
    return () => { cancel = true }
  }, [projectId])

  const [medView, setMedView] = useState('combined') // 'A2'|'A3'|'combined'
  const [importing, setImporting] = useState(false)
  const [importLog, setImportLog] = useState([])
  const [batchKeyword, setBatchKeyword] = useState('')
  const [batchEmpresa, setBatchEmpresa] = useState('')
  const [batchOnlyEmpty, setBatchOnlyEmpty] = useState(true)
  const [densityMap, setDensityMap] = useState(() => saved?.densityMap || { ...DEFAULT_DENSITY })

  // Tab 3: Calculadora
  const [elements, setElements] = useState(() => saved?.elements || ELEMENTS_LIST.map(e => newElem(e.key)))
  const [maderaLegal, setMaderaLegal] = useState(() => saved?.maderaLegal ?? false)
  const [openElems, setOpenElems] = useState(() => new Set())

  // Reset elements + maderaLegal cuando cambia projectId — evita arrastre entre proyectos
  const prevProjectIdRef = useRef(projectId)
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId
      const fresh = loadState(projectId)
      setElements(fresh?.elements || ELEMENTS_LIST.map(e => newElem(e.key)))
      setMaderaLegal(fresh?.maderaLegal ?? false)
    }
  }, [projectId])

  // Auto-aplicar partidas → elements SIEMPRE al cargar partidas+certs frescas de BD.
  // Esto garantiza que el cálculo refleja la versión actual del algoritmo (con catálogo de
  // densidades) y NO depende del state cached en localStorage. Es la fuente de verdad para
  // calculadora MAT 3: partidas + certs (en BD) → elements (derivado en cada carga).
  useEffect(() => {
    if (autoAppliedRef.current) return
    if (!partidas.length || !certs.length) return
    const newElems = partidasToElements(partidas, certs)
    if (!newElems.some(e => e.presente)) return
    autoAppliedRef.current = true
    setElements(newElems)
  }, [partidas, certs])

  const [uploadingCert, setUploadingCert] = useState(null)
  const [savedOk, setSavedOk] = useState(false)
  const hasMounted = useRef(false)

  // Persistencia — solo después del primer render (no en mount)
  useEffect(() => {
    if (!hasMounted.current) { hasMounted.current = true; return }
    saveState(projectId, { certs, partidas, densityMap, elements, maderaLegal })
  }, [certs, partidas, densityMap, elements, maderaLegal, projectId])

  function saveNow() {
    saveState(projectId, { certs, partidas, densityMap, elements, maderaLegal })
    savePartidasToSupabase(projectId, partidas)
    saveCertsToSupabase(projectId, certs)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  const calc = calcMAT3(elements)

  // ── Helpers certificaciones ───────────────────────────────────────────────
  function setCert(id, field, val) {
    setCerts(prev => {
      const updated = prev.map(c => c.id === id ? { ...c, [field]: field === 'level' ? parseInt(val) : val } : c)
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, certs: updated })
      saveCertsToSupabase(projectId, updated)
      return updated
    })
  }
  function addCert() {
    setCerts(prev => {
      const updated = [...prev, { id: Math.random(), empresa: '', certType: 'SGA', level: 3, certPath: null }]
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, certs: updated })
      saveCertsToSupabase(projectId, updated)
      return updated
    })
  }
  function removeCert(id) {
    setCerts(prev => {
      const updated = prev.filter(c => c.id !== id)
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, certs: updated })
      saveCertsToSupabase(projectId, updated)
      return updated
    })
  }

  // ── Subir certificado ─────────────────────────────────────────────────────
  async function uploadCertificate(certId, file) {
    const cert = certs.find(c => c.id === certId)
    if (!cert || !file) return
    setUploadingCert(certId)
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `mat3/certs/${projectId}/${Date.now()}_${safeName}`
      const { error } = await supabase.storage.from('evidences').upload(path, file, { upsert: true })
      if (error) throw error
      // Actualizar certPath y certName en una sola llamada para evitar race condition
      setCerts(prev => {
        const updated = prev.map(c => c.id === certId ? { ...c, certPath: path, certName: file.name } : c)
        const current = loadState(projectId) || {}
        saveState(projectId, { ...current, certs: updated })
        saveCertsToSupabase(projectId, updated)
        return updated
      })
    } catch (e) {
      alert('Error subiendo certificado: ' + e.message)
    }
    setUploadingCert(null)
  }

  // ── Importar presupuestos desde biblioteca ────────────────────────────────
  async function importFromLibrary() {
    setImporting(true)
    setImportLog([])
    const log = (msg) => setImportLog(prev => [...prev, msg])

    try {
      // 1. Obtener docs del proyecto
      const { data: docs } = await supabase.from('project_documents')
        .select('*').eq('project_id', projectId)
      if (!docs?.length) throw new Error('No hay documentos en la biblioteca del proyecto.')

      // 2. Filtrar presupuestos
      const presupDocs = docs.filter(d =>
        d.file_name?.toLowerCase().includes('presup') ||
        d.file_name?.toLowerCase().includes('budget')
      )
      if (!presupDocs.length) throw new Error('No se encontraron archivos de presupuesto (con "Presup" en el nombre) en la biblioteca.')

      log(`Encontrados ${presupDocs.length} presupuesto(s): ${presupDocs.map(d => d.file_name).join(', ')}`)


      const allPartidas = []

      for (const doc of presupDocs) {
        const source = doc.file_name.toLowerCase().includes('a2') ? 'A2'
                     : doc.file_name.toLowerCase().includes('a3') ? 'A3' : 'General'
        log(`📄 Procesando ${doc.file_name} (${source})...`)

        // Descargar PDF
        const { data: signedData } = await supabase.storage
          .from('evidences').createSignedUrl(doc.storage_path, 3600)
        if (!signedData?.signedUrl) { log(`⚠️ No se pudo obtener URL de ${doc.file_name}`); continue }

        const pdfResp = await fetch(signedData.signedUrl)
        if (!pdfResp.ok) { log(`⚠️ Error descargando ${doc.file_name}`); continue }

        const blob = await pdfResp.blob()
        const file = new File([blob], doc.file_name, { type: 'application/pdf' })

        // Dividir en chunks
        log(`Dividiendo en partes de máx. 20 páginas...`)
        const chunks = await splitPdf(file, 20)
        log(`${chunks.length} parte(s) — enviando a Claude...`)

        for (const chunk of chunks) {
          const label = chunks.length > 1 ? ` parte ${chunk.chunkIndex + 1}/${chunk.chunkTotal}` : ''
          log(`  Analizando${label} (${chunk.pages} páginas)...`)

          // Convertir chunk a base64
          const arrayBuffer = await chunk.file.arrayBuffer()
          const bytes = new Uint8Array(arrayBuffer)
          let binary = ''
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
          const base64 = btoa(binary)

          try {
            const { data, error } = await supabase.functions.invoke('extract-mat3-budget', {
              body: { pdfBase64: base64, fileName: doc.file_name, chunkIndex: chunk.chunkIndex, chunkTotal: chunk.chunkTotal }
            })
            if (error) throw new Error(error.message || 'Error en la Edge Function')
            if (!data?.ok) throw new Error(data?.error || 'Error desconocido en la Edge Function')
            const extracted = data.partidas || []
            if (extracted.length) {
              const tagged = extracted.map(p => ({ ...p, id: Math.random(), source, densityFactor: '' }))
              allPartidas.push(...tagged)
              log(`  ✓ ${extracted.length} partidas extraídas`)
            } else {
              log(`  ℹ️ Sin partidas de material en estas páginas`)
            }
          } catch (e) {
            log(`  ⚠️ Error en extracción: ${e.message}`)
          }
        }
      }

      if (!allPartidas.length) {
        log('⚠️ No se extrajeron partidas. Verifica que los documentos sean presupuestos de obra.')
      } else {
        setPartidas(allPartidas)
        // Guardar inmediatamente en localStorage y Supabase
        const current = loadState(projectId) || {}
        saveState(projectId, { ...current, partidas: allPartidas })
        savePartidasToSupabase(projectId, allPartidas)
        log(`✅ Total: ${allPartidas.length} partidas extraídas. Revisa y confirma antes de aplicar a la calculadora.`)
      }
    } catch (e) {
      log(`❌ Error: ${e.message}`)
    }
    setImporting(false)
  }

  // ── Asignación masiva de empresa ─────────────────────────────────────────
  function batchAssign() {
    if (!batchEmpresa.trim()) return alert('Escribe una empresa para asignar.')
    const kw = batchKeyword.trim().toLowerCase()
    setPartidas(prev => {
      const updated = prev.map(p => {
        const matchDesc = !kw || (p.description || '').toLowerCase().includes(kw)
        const matchEmpty = !batchOnlyEmpty || !p.empresa
        if (matchDesc && matchEmpty) return { ...p, empresa: batchEmpresa.trim() }
        return p
      })
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, partidas: updated })
      savePartidasToSupabase(projectId, updated)
      return updated
    })
  }

  // ── Eliminar partidas sin empresa ─────────────────────────────────────────
  function deleteEmptyEmpresa() {
    const count = partidas.filter(p => !p.empresa?.trim()).length
    if (!count) return alert('No hay partidas sin empresa asignada.')
    if (!window.confirm(`¿Eliminar las ${count} partida(s) que tienen el campo empresa vacío?`)) return
    setPartidas(prev => {
      const updated = prev.filter(p => p.empresa?.trim())
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, partidas: updated })
      savePartidasToSupabase(projectId, updated)
      return updated
    })
  }

  // ── Aplicar mediciones → calculadora ─────────────────────────────────────
  function applyToCalculator() {
    const visible = medView === 'combined' ? partidas : partidas.filter(p => p.source === medView)
    if (!visible.length) { alert('No hay partidas para aplicar.'); return }
    const newElems = partidasToElements(visible, certs)
    setElements(newElems)
    setTab('calculadora')
  }

  // ── Helpers calculadora ───────────────────────────────────────────────────
  function setElem(key, fn) { setElements(prev => prev.map(e => e.key === key ? fn(e) : e)) }
  function togglePresente(key) { setElem(key, e => ({ ...e, presente: !e.presente })) }
  function setSpecCount(key, n) {
    setElem(key, e => {
      let specs = [...e.specs]
      while (specs.length < n) specs.push(newSpec())
      specs = specs.slice(0, n)
      const each = (100 / n).toFixed(1)
      specs = n > 1 ? specs.map(s => ({ ...s, weight: each })) : [{ ...specs[0], weight: '100' }]
      return { ...e, specs }
    })
  }
  function setSpecWeight(key, si, val) {
    setElem(key, e => ({ ...e, specs: e.specs.map((s, i) => i === si ? { ...s, weight: val } : s) }))
  }
  function addMaterial(key, si) {
    setElem(key, e => ({ ...e, specs: e.specs.map((s, i) => i === si ? { ...s, materials: [...s.materials, newMat()] } : s) }))
  }
  function removeMaterial(key, si, mi) {
    setElem(key, e => ({ ...e, specs: e.specs.map((s, i) => {
      if (i !== si) return s
      const mats = s.materials.filter((_, j) => j !== mi)
      return { ...s, materials: mats.length ? mats : [newMat()] }
    })}))
  }
  function setMat(key, si, mi, field, val) {
    setElem(key, e => ({ ...e, specs: e.specs.map((s, i) => {
      if (i !== si) return s
      return { ...s, materials: s.materials.map((m, j) => j === mi ? { ...m, [field]: val } : m) }
    })}))
  }

  async function handleSave() {
    if (!maderaLegal) { alert('El prerrequisito de madera legal debe estar confirmado antes de guardar.'); return }
    await onSave({ pts: calc.breeamPts, innovPts: calc.innovPts, pct: calc.pct, mat3State: { elements, maderaLegal, certs } })
  }

  // El botón CAL MAT3 (descarga Calculadora oficial .xlsm con macros VBA) vive
  // ahora en App.js, en la fila estándar de botones del criterio.

  // ── Export Excel (3 hojas: Aprovisionamiento + Mediciones + Resumen) ──────
  function exportToExcel() {
    const wb = XLSX.utils.book_new()

    // ── Hoja 1: Aprovisionamiento ──────────────────────────────────────────
    const certRows = certs.map(c => {
      const tier = TIER_INFO.find(t => t.value === c.level)
      return {
        'Empresa / proveedor': c.empresa || '',
        'Tipo de certificación': c.certType || '',
        'Nivel BREEAM (1-4)': c.level ?? 4,
        'Puntos por elemento (Tabla 34)': tier?.points ?? 0,
        'Descripción nivel': tier?.label?.replace(/^Nivel \d – /, '') || '',
        'Certificado PDF subido': c.certPath ? (c.certName || 'Sí') : 'No',
      }
    })
    const ws1 = XLSX.utils.json_to_sheet(certRows)
    ws1['!cols'] = [{ wch: 32 }, { wch: 20 }, { wch: 16 }, { wch: 26 }, { wch: 40 }, { wch: 28 }]
    XLSX.utils.book_append_sheet(wb, ws1, 'Aprovisionamiento')

    // ── Hoja 2: Mediciones ─────────────────────────────────────────────────
    const partRows = partidas.map(p => {
      const certLevel = certs.find(c => c.empresa.toLowerCase() === (p.empresa || '').toLowerCase())?.level || 4
      const tier = TIER_INFO.find(t => t.value === certLevel)
      const factor = parseFloat(p.densityFactor) || densityMap[p.unit] || 1
      const kg = (parseFloat(p.qty) || 0) * factor
      return {
        'Código partida': p.code || '',
        'Descripción': p.description || '',
        'Empresa / proveedor': p.empresa || '',
        'Unidad': p.unit || '',
        'Cantidad': parseFloat(p.qty) || 0,
        'Factor densidad (kg/ud)': factor,
        'Equivalente kg': Math.round(kg * 100) / 100,
        'Nivel BREEAM': certLevel,
        'Puntos/elemento': tier?.points ?? 0,
        'Elemento BREEAM': ELEMENT_LABEL[p.elementBREEAM] || p.elementBREEAM,
        'Fuente': p.source || 'manual',
      }
    })
    const ws2 = XLSX.utils.json_to_sheet(partRows)
    ws2['!cols'] = [
      { wch: 14 }, { wch: 60 }, { wch: 28 }, { wch: 8 }, { wch: 12 },
      { wch: 18 }, { wch: 14 }, { wch: 13 }, { wch: 15 }, { wch: 28 }, { wch: 10 },
    ]
    XLSX.utils.book_append_sheet(wb, ws2, 'Mediciones')

    // ── Hoja 3: Resumen por elemento ───────────────────────────────────────
    const summary = ELEMENTS_LIST.map(({ key, label }) => {
      const group = partidas.filter(p => p.elementBREEAM === key)
      let totalKg = 0, respKg = 0
      for (const p of group) {
        const factor = parseFloat(p.densityFactor) || densityMap[p.unit] || 1
        const kg = (parseFloat(p.qty) || 0) * factor
        const certLevel = certs.find(c => c.empresa.toLowerCase() === (p.empresa || '').toLowerCase())?.level || 4
        totalKg += kg
        if (certLevel <= 3) respKg += kg
      }
      const pctResp = totalKg > 0 ? (respKg / totalKg * 100) : 0
      const elemRes = calc.results?.[key]
      const presente = elements.find(e => e.key === key)?.presente
      return {
        'Elemento BREEAM (Tabla 36)': label,
        'Presente en proyecto': presente ? 'Sí' : 'No',
        'Nº partidas evaluadas': group.length,
        'Total kg equivalentes': Math.round(totalKg * 100) / 100,
        'kg responsable (nivel 1-3)': Math.round(respKg * 100) / 100,
        '% responsable (≥80% para puntuar)': Math.round(pctResp * 10) / 10,
        'Cumple regla 80%': pctResp >= 80 ? 'Sí' : 'No',
        'Puntos disponibles (Tabla 34)': presente ? 3.0 : 0,
        'Puntos obtenidos elemento': elemRes ? Math.round(elemRes.elementPts * 100) / 100 : 0,
      }
    })
    // Fila total global
    summary.push({
      'Elemento BREEAM (Tabla 36)': '— TOTAL —',
      'Presente en proyecto': `${calc.totalAvail / 3} elementos presentes`,
      'Nº partidas evaluadas': partidas.length,
      'Total kg equivalentes': '',
      'kg responsable (nivel 1-3)': '',
      '% responsable (≥80% para puntuar)': '',
      'Cumple regla 80%': '',
      'Puntos disponibles (Tabla 34)': calc.totalAvail,
      'Puntos obtenidos elemento': Math.round(calc.totalEarned * 100) / 100,
    })
    summary.push({
      'Elemento BREEAM (Tabla 36)': '% Calculadora MAT 3',
      'Presente en proyecto': `${(Math.round(calc.pct * 10) / 10).toFixed(1)} %`,
      'Nº partidas evaluadas': '',
      'Total kg equivalentes': '',
      'kg responsable (nivel 1-3)': '',
      '% responsable (≥80% para puntuar)': '',
      'Cumple regla 80%': '',
      'Puntos disponibles (Tabla 34)': '',
      'Puntos obtenidos elemento': `${calc.breeamPts}/3 pts BREEAM${calc.innovPts ? ' + 1 INO 1 (Nivel Ejemplar)' : ''}`,
    })
    const ws3 = XLSX.utils.json_to_sheet(summary)
    ws3['!cols'] = [{ wch: 38 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 24 }, { wch: 28 }, { wch: 16 }, { wch: 22 }, { wch: 22 }]
    XLSX.utils.book_append_sheet(wb, ws3, 'Resumen MAT 3')

    // Generar fichero
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/octet-stream' })
    const today = new Date().toISOString().slice(0, 10)
    saveAs(blob, `MAT3_Aprovisionamiento_Responsable_${today}.xlsx`)
  }

  const fmt = v => typeof v === 'number' ? v.toFixed(2) : '–'
  const fmtPct = v => typeof v === 'number' ? v.toFixed(1) + '%' : '–'
  const presentCount = elements.filter(e => e.presente).length

  // ── Agrupación mediciones por elemento ───────────────────────────────────
  const visiblePartidas = medView === 'combined' ? partidas : partidas.filter(p => p.source === medView)
  const groupedPartidas = {}
  for (const p of visiblePartidas) {
    if (!groupedPartidas[p.elementBREEAM]) groupedPartidas[p.elementBREEAM] = []
    groupedPartidas[p.elementBREEAM].push(p)
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const tabStyle = (active) => ({
    padding: '0.5rem 1rem', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: active ? '700' : '400',
    color: active ? C.olive : C.muted, background: 'transparent', borderBottom: active ? `2px solid ${C.olive}` : '2px solid transparent',
    fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  })

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.dark }}>
      <datalist id="empresas-list">
        {certs.map(c => <option key={c.id} value={c.empresa} />)}
      </datalist>

      {/* ── Header con resultado ── */}
      <div style={{ ...CARD, marginBottom: '0.875rem', borderLeft: `4px solid ${C.amber}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: C.amber, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              BREEAM ES NC 2015 — MAT 3
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '700', marginTop: '0.15rem' }}>Aprovisionamiento Responsable de Materiales</div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', background: C.cream, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '0.4rem 0.75rem' }}>
              <div style={{ fontSize: '0.58rem', color: C.muted, textTransform: 'uppercase' }}>% Conseguido</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: calc.pct >= 54 ? C.olive : calc.pct >= 18 ? C.amber : C.coral }}>{fmtPct(calc.pct)}</div>
            </div>
            <div style={{ textAlign: 'center', background: calc.breeamPts > 0 ? '#F0F4E8' : '#FEF2F2', border: `1px solid ${calc.breeamPts > 0 ? C.olive : '#FCA5A5'}`, borderRadius: '6px', padding: '0.4rem 0.75rem' }}>
              <div style={{ fontSize: '0.58rem', color: C.muted, textTransform: 'uppercase' }}>Puntos BREEAM</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: calc.breeamPts > 0 ? C.olive : C.coral }}>
                {calc.breeamPts}/3{calc.innovPts ? ' ★' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={exportToExcel} title="Descargar Excel con 3 hojas: Aprovisionamiento, Mediciones y Resumen MAT 3" style={{ ...BTN_G, borderColor: C.blue, color: C.blue }}>
                <DownloadSimple size={14} /> Descargar Excel
              </button>
              <button onClick={saveNow} style={{ ...BTN_G, borderColor: savedOk ? C.olive : C.border, color: savedOk ? C.olive : C.muted }}>
                <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
              </button>
              <button onClick={handleSave} disabled={saving} style={{ ...BTN_P, opacity: saving ? 0.7 : 1 }}>
                <FloppyDisk size={15} />{saving ? 'Guardando…' : 'Aplicar puntuación'}
              </button>
            </div>
          </div>
        </div>
        {/* Prerrequisito madera */}
        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: maderaLegal ? '#F0F4E8' : '#FFF7ED', border: `1px solid ${maderaLegal ? '#C6D88A' : '#FED7AA'}`, borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <input type="checkbox" id="maderaLegal" checked={maderaLegal} onChange={e => setMaderaLegal(e.target.checked)} style={{ width: 14, height: 14, accentColor: C.olive, cursor: 'pointer' }} />
          <label htmlFor="maderaLegal" style={{ fontSize: '0.76rem', cursor: 'pointer', color: maderaLegal ? C.olive : C.amber, fontWeight: maderaLegal ? '600' : '400' }}>
            <strong>Prerrequisito:</strong> Toda la madera del proyecto es legalmente aprovechada y comercializada (FSC, PEFC o equivalente)
          </label>
        </div>
        {/* Escala puntos */}
        <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: C.muted }}>
          {[{p:70,l:'≥70% → 3pts + Ejemplar ★'},{p:54,l:'≥54% → 3pts'},{p:36,l:'≥36% → 2pts'},{p:18,l:'≥18% → 1pt'},{p:-1,l:'<18% → 0pts'}].map(({p,l},i) => (
            <span key={i} style={{ marginRight: '0.75rem', color: calc.pct >= p && (i===0 ? calc.pct>=70 : true) && (i>0 ? calc.pct<[70,70,54,36,18][i] || p===-1 : true) ? C.olive : C.muted }}>{l}</span>
          ))}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ borderBottom: `1px solid ${C.border}`, marginBottom: '0.875rem', background: C.card, borderRadius: '6px 6px 0 0', padding: '0 0.5rem' }}>
        <button style={tabStyle(tab === 'aprovisionamiento')} onClick={() => setTab('aprovisionamiento')}>
          <Buildings size={14} />Aprovisionamiento
        </button>
        <button style={tabStyle(tab === 'mediciones')} onClick={() => setTab('mediciones')}>
          <Table size={14} />Mediciones
        </button>
        <button style={tabStyle(tab === 'calculadora')} onClick={() => setTab('calculadora')}>
          <Calculator size={14} />Calculadora
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 1 — APROVISIONAMIENTO (MAT3.2)
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'aprovisionamiento' && (
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Tabla de Aprovisionamiento Responsable
              </div>
              <div style={{ fontSize: '0.73rem', color: C.muted, marginTop: '0.15rem' }}>
                Asigna el nivel de certificación BREEAM a cada empresa suministradora. Sube los certificados en PDF.
              </div>
            </div>
            <button onClick={addCert} style={{ ...BTN_G }}>
              <Plus size={13} />Añadir empresa
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: C.cream }}>
                  {['Empresa / Fabricante', 'Tipo cert.', 'Nivel BREEAM', 'Puntos/mat.', 'Certificado', ''].map((h, i) => (
                    <th key={i} style={{ padding: '0.45rem 0.6rem', textAlign: i >= 2 ? 'center' : 'left', fontWeight: '600', color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {certs.map(c => {
                  const tierInfo = TIER_INFO.find(t => t.value === c.level)
                  return (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '0.35rem 0.6rem' }}>
                        <input value={c.empresa} onChange={e => setCert(c.id, 'empresa', e.target.value)}
                          placeholder="Nombre empresa" style={{ ...INP }} />
                      </td>
                      <td style={{ padding: '0.35rem 0.6rem', width: '110px' }}>
                        <select value={c.certType} onChange={e => setCert(c.id, 'certType', e.target.value)} style={{ ...INP }}>
                          {['SGA', 'BES', 'FSC', 'PEFC', 'EMAS', '(-)'].map(t => <option key={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem 0.6rem', width: '200px' }}>
                        <select value={c.level} onChange={e => setCert(c.id, 'level', e.target.value)} style={{ ...INP }}>
                          {TIER_INFO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'center', fontWeight: '700', color: tierInfo?.color || C.muted }}>
                        {tierInfo?.points ?? '–'}
                      </td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'center' }}>
                        {c.certPath ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'center' }}>
                            <CheckCircle size={14} color={C.olive} weight="fill" />
                            <span style={{ fontSize: '0.7rem', color: C.olive }}>{c.certName || 'Subido'}</span>
                            <button onClick={() => { setCert(c.id, 'certPath', null); setCert(c.id, 'certName', null) }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 0 }}>
                              <X size={11} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <input type="file" accept=".pdf" style={{ display: 'none' }} id={`cert-${c.id}`}
                              onChange={e => e.target.files?.[0] && uploadCertificate(c.id, e.target.files[0])} />
                            <label htmlFor={`cert-${c.id}`} style={{ ...BTN_G, cursor: 'pointer', fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}>
                              {uploadingCert === c.id ? <Spinner size={12} className="spin" /> : <UploadSimple size={12} />}
                              Subir PDF
                            </label>
                          </>
                        )}
                      </td>
                      <td style={{ padding: '0.35rem 0.3rem', width: '30px' }}>
                        <button onClick={() => removeCert(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.coral, padding: 0, display: 'flex' }}>
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '0.875rem', padding: '0.5rem 0.75rem', background: C.cream, borderRadius: '4px', fontSize: '0.71rem', color: C.muted, lineHeight: 1.5 }}>
            <strong style={{ color: C.brown }}>Tipos de certificación:</strong>{' '}
            SGA = Sistema de Gestión Ambiental (ISO 14001/EMAS) → Nivel 3 |{' '}
            BES = BES 6001 (Responsible Sourcing) → Nivel 1 |{' '}
            FSC/PEFC = Chain of Custody (madera) → Nivel 1 |{' '}
            (-) = Sin certificación → Nivel 4
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 2 — MEDICIONES (MAT3.3)
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'mediciones' && (
        <div>
          {/* Barra de acciones */}
          <div style={{ ...CARD, marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={importFromLibrary} disabled={importing} style={{ ...BTN_P, opacity: importing ? 0.7 : 1 }}>
              {importing ? <Spinner size={14} className="spin" /> : <ArrowsClockwise size={14} />}
              {importing ? 'Importando…' : 'Importar presupuestos de la biblioteca'}
            </button>
            <button onClick={() => setPartidas(prev => [...prev, newPartida()])} style={BTN_G}>
              <Plus size={13} />Añadir partida manual
            </button>
            {partidas.length > 0 && (
              <button onClick={applyToCalculator} style={{ ...BTN_G, color: C.olive, borderColor: C.olive }}>
                <Calculator size={13} />Aplicar a calculadora →
              </button>
            )}
            {partidas.some(p => !p.empresa?.trim()) && (
              <button onClick={deleteEmptyEmpresa} style={{ ...BTN_G, color: C.coral, borderColor: C.coral }}>
                <Trash size={13} />Eliminar sin empresa ({partidas.filter(p => !p.empresa?.trim()).length})
              </button>
            )}
            {/* Filtro fuente */}
            {partidas.some(p => p.source === 'A2' || p.source === 'A3') && (
              <div style={{ display: 'flex', gap: '0.3rem', marginLeft: 'auto' }}>
                {['combined', 'A2', 'A3'].map(v => (
                  <button key={v} onClick={() => setMedView(v)} style={{ padding: '0.25rem 0.6rem', border: `1px solid ${medView === v ? C.olive : C.border}`, borderRadius: '4px', background: medView === v ? '#F0F4E8' : 'white', color: medView === v ? C.olive : C.muted, cursor: 'pointer', fontSize: '0.73rem', fontWeight: medView === v ? '700' : '400' }}>
                    {v === 'combined' ? 'Combinado' : v}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Log de importación */}
          {importLog.length > 0 && (
            <div style={{ ...CARD, marginBottom: '0.75rem', background: '#1E1E1E', color: '#C8F0C8', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', padding: '0.75rem 1rem', maxHeight: '160px', overflowY: 'auto' }}>
              {importLog.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          )}

          {/* Asignación masiva */}
          {partidas.length > 0 && (
            <div style={{ ...CARD, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Asignar por lotes</span>
              <input
                value={batchKeyword}
                onChange={e => setBatchKeyword(e.target.value)}
                placeholder="Filtrar por palabra (ej: acero)"
                style={{ ...INP, flex: '1', minWidth: '140px' }}
              />
              <input
                list="empresas-list"
                value={batchEmpresa}
                onChange={e => setBatchEmpresa(e.target.value)}
                placeholder="Empresa a asignar"
                style={{ ...INP, flex: '1', minWidth: '140px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: C.muted, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={batchOnlyEmpty} onChange={e => setBatchOnlyEmpty(e.target.checked)} />
                Solo vacíos
              </label>
              <button onClick={batchAssign} style={{ ...BTN_G, color: C.olive, borderColor: C.olive, whiteSpace: 'nowrap' }}>
                Asignar →
              </button>
            </div>
          )}

          {/* Tabla de partidas */}
          {visiblePartidas.length === 0 ? (
            <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem', color: C.muted }}>
              <Table size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
              <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Sin datos de mediciones</div>
              <div style={{ fontSize: '0.75rem' }}>Importa los presupuestos desde la biblioteca o añade partidas manualmente.</div>
            </div>
          ) : (
            ELEMENTS_LIST.map(({ key, label }) => {
              const group = groupedPartidas[key] || []
              if (!group.length) return null
              // Calcular total kg y responsable por elemento
              let totalKg = 0, respKg = 0
              for (const p of group) {
                const factor = parseFloat(p.densityFactor) || densityMap[p.unit] || 1
                const kg = (parseFloat(p.qty) || 0) * factor
                const certLevel = certs.find(c => c.empresa.toLowerCase() === (p.empresa || '').toLowerCase())?.level || 4
                totalKg += kg
                if (certLevel <= 3) respKg += kg
              }
              const elemResp = totalKg > 0 ? (respKg / totalKg * 100) : 0
              return (
                <div key={key} style={{ ...CARD, marginBottom: '0.75rem', padding: 0 }}>
                  <div style={{ padding: '0.6rem 1rem', background: C.cream, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.brown }}>{label}</span>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: '0.73rem', color: elemResp >= 80 ? C.olive : C.coral }}>
                      {elemResp >= 80 ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
                      <span>{fmtPct(elemResp)} responsable</span>
                      <span style={{ color: C.muted }}>·</span>
                      <span style={{ color: C.muted }}>{group.length} partidas</span>
                    </div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr style={{ background: '#FAFAFA' }}>
                        {['Empresa', 'Descripción', 'Ud.', 'Cantidad', 'Factor densidad', 'KG equiv.', 'Nivel', 'Elemento', ''].map((h, i) => (
                          <th key={i} style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: '600', color: C.muted, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.map((p, pi) => {
                        const certLevel = certs.find(c => c.empresa.toLowerCase() === (p.empresa || '').toLowerCase())?.level || 4
                        const factor = parseFloat(p.densityFactor) || densityMap[p.unit] || 1
                        const kg = (parseFloat(p.qty) || 0) * factor
                        const tierInfo = TIER_INFO.find(t => t.value === certLevel)
                        const updateP = (field, val) => setPartidas(prev => prev.map(x => x.id === p.id ? { ...x, [field]: val } : x))
                        const saveP = () => setPartidas(prev => {
                          const current = loadState(projectId) || {}
                          saveState(projectId, { ...current, partidas: prev })
                          savePartidasToSupabase(projectId, prev)
                          return prev
                        })
                        return (
                          <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <input
                                list="empresas-list"
                                value={p.empresa || ''}
                                onChange={e => updateP('empresa', e.target.value)}
                                onBlur={saveP}
                                placeholder="— asignar —"
                                style={{ ...INP, width: '120px', borderColor: !p.empresa ? '#E8A838' : INP.borderColor, background: !p.empresa ? '#FFFBF0' : INP.background }}
                              />
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <input value={p.description || ''} onChange={e => updateP('description', e.target.value)} placeholder="Descripción" style={{ ...INP, width: '180px' }} />
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <select value={p.unit || 'kg'} onChange={e => updateP('unit', e.target.value)} style={{ ...INP, width: '60px' }}>
                                {['kg', 'm3', 't', 'm2', 'm', 'ud', 'ml'].map(u => <option key={u}>{u}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <input type="number" value={p.qty || ''} onChange={e => updateP('qty', e.target.value)} placeholder="0" style={{ ...INP, width: '75px', textAlign: 'right' }} />
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <input type="number" value={p.densityFactor || ''} onChange={e => updateP('densityFactor', e.target.value)}
                                placeholder={densityMap[p.unit] != null ? String(densityMap[p.unit]) : 'kg/ud'} style={{ ...INP, width: '75px', textAlign: 'right' }} />
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem', fontFamily: "'IBM Plex Mono',monospace", fontWeight: '600', color: certLevel <= 3 ? C.olive : C.muted, textAlign: 'right' }}>
                              {kg > 0 ? (kg >= 1000 ? (kg/1000).toFixed(1) + 't' : kg.toFixed(0) + '') : '–'}
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <span style={{ fontSize: '0.7rem', color: tierInfo?.color, fontWeight: '600' }}>N{certLevel}</span>
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <select value={p.elementBREEAM || 'estructura'} onChange={e => updateP('elementBREEAM', e.target.value)} style={{ ...INP, width: '130px' }}>
                                {ELEMENTS_LIST.map(e => <option key={e.key} value={e.key}>{e.label.substring(0, 20)}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '0.3rem 0.3rem' }}>
                              <button onClick={() => setPartidas(prev => {
                                const updated = prev.filter(x => x.id !== p.id)
                                const current = loadState(projectId) || {}
                                saveState(projectId, { ...current, partidas: updated })
                                savePartidasToSupabase(projectId, updated)
                                return updated
                              })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.coral, padding: 0, display: 'flex' }}>
                                <Trash size={13} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })
          )}

          {/* Factores de densidad globales */}
          {partidas.length > 0 && (
            <div style={{ ...CARD, marginTop: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                Factores de conversión por defecto (kg por unidad)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {['m3', 't', 'm2', 'm', 'ud', 'ml'].map(unit => (
                  <div key={unit} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.73rem', color: C.muted, minWidth: '30px' }}>{unit}:</label>
                    <input type="number" value={densityMap[unit] ?? ''} onChange={e => setDensityMap(prev => ({ ...prev, [unit]: parseFloat(e.target.value) || null }))}
                      placeholder="—" style={{ ...INP, width: '70px', textAlign: 'right' }} />
                    <span style={{ fontSize: '0.68rem', color: C.muted }}>kg/{unit}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: C.muted }}>
                Hormigón ≈ 2400 kg/m³ · Acero ≈ 7850 kg/m³ · Madera ≈ 500 kg/m³ · Si el material ya viene en kg, factor = 1
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 3 — CALCULADORA (MAT3.4)
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'calculadora' && (
        <div>
          {/* Tabla resumen elementos */}
          <div style={{ ...CARD, marginBottom: '0.875rem', padding: 0 }}>
            <div style={{ padding: '0.6rem 1rem', borderBottom: `1px solid ${C.border}`, fontSize: '0.7rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Elementos de construcción</span>
              <span style={{ fontWeight: '400', color: C.muted }}>{presentCount} presentes · {calc.totalAvail} pts disponibles</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: C.cream }}>
                  {['Elemento', 'Presente', '% Resp.', 'Pts elem.', '≥80%'].map((h, i) => (
                    <th key={i} style={{ padding: '0.45rem 0.75rem', textAlign: i <= 1 ? 'left' : 'right', fontWeight: '600', color: C.muted, borderBottom: `1px solid ${C.border}`, width: i === 1 ? '70px' : i >= 2 ? '85px' : 'auto' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ELEMENTS_LIST.filter(({ key }) => {
                  // Ocultar elementos sin partidas ni materiales manuales rellenados.
                  // Ejemplo: E9 Paisaje ajardinado duro se excluye por NA4 cuando la superficie
                  // exterior es menor que la construida (Manual BREEAM ES NC 2015 MAT 3).
                  // Nota: no basta con elem.presente (localStorage puede tener estado stale) —
                  // requerimos evidencia real: partidas o materiales con % > 0.
                  const elem = elements.find(e => e.key === key)
                  const hasPartidas = (groupedPartidas[key] || []).length > 0
                  const hasMateriales = elem?.specs?.some(s =>
                    s.materials?.some(m => parseFloat(m.pct) > 0)
                  )
                  return hasPartidas || hasMateriales
                }).map(({ key, label }) => {
                  const elem = elements.find(e => e.key === key)
                  const r = elem?.presente ? calc.results[key] : null
                  const isOpen = openElems.has(key)
                  return (
                    <tr key={key} style={{ borderBottom: `1px solid ${C.border}`, background: isOpen ? '#F9F7F5' : 'white' }}>
                      <td style={{ padding: '0.4rem 0.75rem' }}>
                        <button onClick={() => setOpenElems(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s })}
                          style={{ background: 'none', border: 'none', cursor: elem?.presente ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '0.35rem', color: C.dark, fontSize: '0.78rem', padding: 0, fontFamily: "'DM Sans', sans-serif" }}>
                          {elem?.presente ? (isOpen ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />) : <span style={{ width: 11 }} />}
                          {label}
                        </button>
                      </td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>
                        <input type="checkbox" checked={elem?.presente ?? false} onChange={() => togglePresente(key)} style={{ width: 14, height: 14, accentColor: C.olive, cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: r ? (r.responsible >= 80 ? C.olive : C.coral) : C.muted, fontWeight: r ? '600' : '400' }}>{r ? fmtPct(r.responsible) : '—'}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace", fontWeight: '600', color: r && r.elementPts > 0 ? C.olive : r ? C.coral : C.muted }}>{r ? fmt(r.elementPts) : '—'}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right' }}>
                        {r ? r.passes80 ? <CheckCircle size={15} color={C.olive} weight="fill" /> : <WarningCircle size={15} color={C.coral} weight="fill" /> : <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#F9F7F5', borderTop: `2px solid ${C.border}` }}>
                  <td colSpan={2} style={{ padding: '0.45rem 0.75rem', fontWeight: '700', fontSize: '0.78rem', color: C.brown }}>Total</td>
                  <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: '700', color: C.dark }}>{calc.totalAvail > 0 ? fmtPct(calc.pct) : '—'}</td>
                  <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: '700', fontFamily: "'IBM Plex Mono',monospace", color: C.dark }}>{fmt(calc.totalEarned)}/{calc.totalAvail}</td>
                  <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: '700', color: calc.breeamPts > 0 ? C.olive : C.coral }}>{calc.breeamPts}/3</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Detalle expandible por elemento */}
          {ELEMENTS_LIST.map(({ key, label }) => {
            const elem = elements.find(e => e.key === key)
            if (!elem?.presente || !openElems.has(key)) return null
            const r = calc.results[key]
            return (
              <div key={key} style={{ ...CARD, marginBottom: '0.75rem', borderLeft: `3px solid ${r?.passes80 ? C.olive : r ? C.coral : C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '700' }}>{label}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {r && (
                      <span style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', borderRadius: '4px', fontWeight: '600', background: r.passes80 ? '#F0F4E8' : '#FEF2F2', color: r.passes80 ? C.olive : C.coral, border: `1px solid ${r.passes80 ? '#C6D88A' : '#FCA5A5'}` }}>
                        {r.passes80 ? `✓ ${fmtPct(r.responsible)} → ${fmt(r.elementPts)} pts` : `✗ ${fmtPct(r.responsible)} (<80%) → 0 pts`}
                      </span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span style={{ fontSize: '0.72rem', color: C.muted }}>Specs:</span>
                      {[1, 2, 3, 4].map(n => (
                        <button key={n} onClick={() => setSpecCount(key, n)} style={{ padding: '0.18rem 0.45rem', border: `1px solid ${elem.specs.length === n ? C.olive : C.border}`, borderRadius: '3px', background: elem.specs.length === n ? '#F0F4E8' : 'white', color: elem.specs.length === n ? C.olive : C.muted, cursor: 'pointer', fontSize: '0.72rem', fontWeight: elem.specs.length === n ? '700' : '400' }}>{n}</button>
                      ))}
                    </div>
                  </div>
                </div>
                {elem.specs.map((spec, si) => (
                  <div key={spec.id} style={{ marginBottom: si < elem.specs.length - 1 ? '0.875rem' : 0, border: `1px solid ${C.border}`, borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{ background: C.cream, padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: '700', color: C.brown }}>Especificación {si + 1}</span>
                      {elem.specs.length > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <label style={{ fontSize: '0.7rem', color: C.muted }}>% del elemento:</label>
                          <input type="number" min="0" max="100" value={spec.weight} onChange={e => setSpecWeight(key, si, e.target.value)} style={{ ...INP, width: '65px' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '0.6rem 0.75rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead>
                          <tr style={{ color: C.muted }}>
                            {['Material', 'Vol / %', 'Certificación', 'Pts contrib.', ''].map((h, i) => (
                              <th key={i} style={{ paddingBottom: '0.35rem', textAlign: i === 3 ? 'right' : 'left', fontWeight: '600' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {spec.materials.map((mat, mi) => {
                            const vals = spec.materials.map(m => Math.max(0, parseFloat(m.pct) || 0))
                            const total = vals.reduce((a, b) => a + b, 0)
                            const pctSpec = total > 0 ? vals[mi] / total * 100 : 0
                            const pctElem = pctSpec * (parseFloat(spec.weight) || 0) / 100
                            const tier = parseInt(mat.tier) || 4
                            const contrib = (pctElem * (TIER_POINTS[tier] || 0)) / 100
                            return (
                              <tr key={mat.id} style={{ borderTop: mi > 0 ? `1px solid ${C.border}` : 'none' }}>
                                <td style={{ paddingTop: '0.35rem', paddingBottom: '0.35rem', paddingRight: '0.5rem' }}>
                                  <input value={mat.name} onChange={e => setMat(key, si, mi, 'name', e.target.value)} placeholder={`Material ${mi + 1}`} style={{ ...INP }} />
                                </td>
                                <td style={{ paddingTop: '0.35rem', paddingRight: '0.5rem', width: '80px' }}>
                                  <input type="number" min="0" value={mat.pct} onChange={e => setMat(key, si, mi, 'pct', e.target.value)} placeholder="%" style={{ ...INP, textAlign: 'right' }} />
                                </td>
                                <td style={{ paddingTop: '0.35rem', paddingRight: '0.5rem', width: '220px' }}>
                                  <select value={mat.tier} onChange={e => setMat(key, si, mi, 'tier', e.target.value)} style={{ ...INP }}>
                                    {TIER_INFO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                  </select>
                                </td>
                                <td style={{ paddingTop: '0.35rem', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace", fontWeight: '600', fontSize: '0.72rem', color: contrib > 0 ? C.olive : C.muted }}>
                                  {vals[mi] > 0 ? fmt(contrib) : '—'}
                                </td>
                                <td style={{ paddingTop: '0.35rem', paddingLeft: '0.4rem', width: '20px' }}>
                                  {spec.materials.length > 1 && (
                                    <button onClick={() => removeMaterial(key, si, mi)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.coral, padding: 0, display: 'flex' }}>
                                      <Trash size={13} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                      {spec.materials.length < 8 && (
                        <button onClick={() => addMaterial(key, si)} style={{ ...BTN_G, marginTop: '0.4rem', fontSize: '0.7rem', padding: '0.2rem 0.55rem' }}>
                          <Plus size={11} />Añadir material
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {r && !r.passes80 && (
                  <div style={{ marginTop: '0.6rem', padding: '0.4rem 0.65rem', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '4px', fontSize: '0.71rem', color: C.coral, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <WarningCircle size={13} weight="fill" />
                    Al menos el 80% de los materiales deben tener certificación responsable (niveles 1–3). Actualmente: {fmtPct(r.responsible)}
                  </div>
                )}
              </div>
            )
          })}

          {/* Leyenda */}
          <div style={{ ...CARD, marginTop: '0.75rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Niveles de certificación (Tabla 37 BREEAM ES NC 2015)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem' }}>
              {TIER_INFO.map(t => (
                <div key={t.value} style={{ padding: '0.4rem 0.6rem', background: C.cream, borderRadius: '4px', borderLeft: `3px solid ${t.color}`, fontSize: '0.71rem' }}>
                  <strong style={{ color: t.color }}>{t.points} pts</strong> — {t.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
